import * as React from 'react'
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react'
import { withTranslation } from 'react-i18next'
import { formatImage, isType } from './utils'
import { isDev } from '../../helpers'
import { ErrorBoundary as Error } from '@christies/react/components/Atoms'
import { Typography, Breakpoints } from '@christies/react/foundations'
import { TFunction } from 'i18next'
import { Endpoint } from './dataFetcher'
import { SocialMediaIcons } from './sharedTypes'
import { ChrComponent } from '../../types'

export type SitecoreGlobal = {
  ismobileapp: boolean
  language: string
  analytics_env: string
  analytics_region_tag: string
  analytics_debug: boolean
  is_authenticated: boolean
  analytics_datalayer: boolean
  interactionDestinationByUrlPath: Record<string, string>
  share: {
    client_id: string
    items: {
      type: SocialMediaIcons
      label_txt: string
      analytics_id: string
      aria_share_title_txt: string
    }[]
  }
  labels: {
    close_modal_txt: string
  }
  access_token_expiration: string
}

export type SitecoreTranslations = {
  departments: string
  storycategories: string
  storymedia: string
  storytypes: string
}

type HeaderFooter = ChrComponent

export type PageSize = Record<keyof typeof Breakpoints, number>

export type AnchorValue = { href: string; linktype?: 'external' | unknown }
export type ImageValue = { src: string; alt: string; width: string; height: string }
export type ImageParsedValue = { src: string; alt: string; aspectRatio?: number }
export type SelectValue<T = string | undefined> = { key: T; value: string; id?: T }
export type RelatedFields = { id: string; title: string }

export type ValueType = AnchorValue | ImageValue | SelectValue | string | number | boolean | undefined
type ValueAttrs = ValueType | ValueType[]

// Sitecore FieldTypes
export type SelectParam<T = string | undefined> = { Key: { value: string }; Value: { value: string }; Id: T }
export type BooleanField = { value: boolean }
export type TextField = { value: string }
export type NumberField = { value: number }
export type SelectField = { fields: SelectParam; id?: string }
export type AnchorField = { value: AnchorValue }
export type ImageField = { value: ImageValue }

export type FieldType =
  | SelectParam
  | BooleanField
  | TextField
  | NumberField
  | SelectField
  | AnchorField
  | ImageField
  | string
  | number
  | boolean
  | undefined

export type FieldAttrs = FieldType | FieldType[]

export type Fields = Record<string, FieldAttrs>

// Parsed FieldTypes
export type ParsedAnchorValue = { href: string; target?: '_blank' }

export type ParsedFieldType =
  | ParsedAnchorValue
  | ImageValue
  | SelectValue
  | RelatedFields
  | number
  | string
  | boolean
  | undefined
export type ParsedField =
  | ParsedFieldType
  | ParsedFieldType[]
  | SitecoreGlobal
  | Endpoint
  | undefined
  | PageSize
  | SitecoreTranslations
  | HeaderFooter
export type ParsedFields = Record<string, ParsedField> | undefined

interface ProvidedProps {
  sitecoreContext: {
    pageEditing: boolean
    route: { fields: Fields }
    site: {}
  }
  fields: Fields
  params: Fields
  rendering: {
    componentName: string
    uid: string
  }
  t: TFunction
}

interface ParsedComponentProps {
  pageEditing: boolean
  parsedFields: ParsedFields
  parsedRouteFields: ParsedFields
  parsedParams: ParsedFields
  parsedSitecoreContext: ParsedFields
  t: TFunction
}

const isAllowedPrimitiveType = (value: FieldAttrs | ValueAttrs): value is string | boolean | number =>
  typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number'

const parseField = (field: FieldAttrs, pageEditing: boolean): ParsedField => {
  if (!field) return
  if (isAllowedPrimitiveType(field)) {
    return field
  }

  if (Array.isArray(field)) {
    return field.map((field) => {
      return isType<SelectField>(field, 'fields')
        ? (parseField({ ...field.fields, Id: field?.id }, pageEditing) as ParsedFieldType)
        : (parseField(field, pageEditing) as ParsedFieldType)
    })
  }

  if (isType<SelectField>(field, 'fields')) {
    const returnValue = { key: field?.fields?.Key?.value, value: field?.fields?.Value?.value }
    if (returnValue) {
      return returnValue
    }
  }

  if (isType<SelectParam>(field, 'Key')) {
    const returnValue = { key: field?.Key?.value, value: field?.Value?.value, id: field?.Id }
    if (returnValue) {
      return returnValue
    }
  }

  if (isType<RelatedFields>(field, 'title')) {
    const returnValue = { title: field?.title, id: field?.id }
    if (returnValue) {
      return returnValue
    }
  }

  if (isType<Endpoint>(field, 'method')) {
    if (field) {
      return field
    }
  }

  if (isType<TextField | NumberField | BooleanField | AnchorField | ImageField>(field, 'value')) {
    const value = field?.value
    if (isAllowedPrimitiveType(value)) {
      return value
    }

    const values = Object.values(value)

    if (values.length && values.some((value) => value)) {
      if (isType<ImageValue>(value, 'src')) {
        return formatImage(value, pageEditing)
      }

      return value
    }
  }
  if (
    isType<SitecoreGlobal>(field, 'ismobileapp') ||
    isType<SitecoreGlobal>(field, 'interactionDestinationByUrlPath') ||
    isType<SitecoreTranslations>(field, 'departments') ||
    isType<PageSize>(field, Breakpoints.xs) ||
    isType<HeaderFooter>(field, 'data')
  ) {
    return field
  }
}

const parseFields = (fields: Fields, pageEditing: boolean): ParsedFields | undefined => {
  if (!fields) return
  const entries = Object.entries(fields).map(([key, field]) => [key, parseField(field, pageEditing)])

  return Object.fromEntries(entries.filter(([, value]) => value !== undefined && value !== null))
}

type ErrorBoundaryProps = { componentName: string; children: React.ReactNode }
type ErrorBoundaryState = { hasError: boolean; error?: Error }
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true }
  }
  componentDidCatch(error: Error): void {
    // You can also log the error to an error reporting service
    if (isDev) {
      console.error(error)
      this.setState({ ...this.state, error: error })
    } else {
      //TODO log this once sentry and app insights is added
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (isDev) {
        return (
          <Error>
            <Typography as="h2">{this.props.componentName}</Typography>
            <Typography as="p">{this.state.error?.name}</Typography>
            <Typography as="p">{this.state.error?.message}</Typography>
          </Error>
        )
      }
      // Do not render component if it crashes on prod
      return null
    }
    return this.props.children
  }
}

const ErrorBoundaryWrapper: React.FC<{ children: React.ReactElement; pageEditing: boolean; componentName: string }> = ({
  children,
  pageEditing,
  componentName,
}) => {
  // we don't want error boundaries in experience editor
  if (pageEditing) {
    return children
  }
  return <ErrorBoundary componentName={componentName}>{children}</ErrorBoundary>
}

export function withParsedContext(Component: React.FC<ParsedComponentProps>): React.FC {
  const WrappedComponent: React.FC<ProvidedProps> = ({ fields, sitecoreContext, params, rendering, t }) => {
    const pageEditing = sitecoreContext?.pageEditing
    const parsedFields = parseFields(fields, pageEditing)

    const filteredContext = Object.fromEntries(
      (Object.entries(sitecoreContext) as [keyof ProvidedProps['sitecoreContext'], string][]).filter(
        ([key]) => key !== 'route' && key !== 'site'
      )
    ) as Record<string, string>

    const parsedRouteFields = parseFields(sitecoreContext.route.fields, pageEditing)
    const parsedParams = parseFields(params, pageEditing)
    const parsedSitecoreContext = parseFields(filteredContext, pageEditing)

    if (!pageEditing && parsedFields?.['Show Component'] === false) return null

    return (
      <ErrorBoundaryWrapper componentName={rendering.componentName} pageEditing={pageEditing}>
        <Component
          parsedFields={parsedFields}
          parsedRouteFields={parsedRouteFields}
          pageEditing={pageEditing}
          parsedParams={parsedParams}
          parsedSitecoreContext={parsedSitecoreContext}
          t={t}
        />
      </ErrorBoundaryWrapper>
    )
  }

  return withSitecoreContext()(withTranslation()(WrappedComponent as React.FC) as React.FC)
}
